import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../../components/layout/Layout';
import PageCard from '../../components/ui/PageCard';
import MagazinePageHeader from './MagazinePageHeader';
import MagazineNewButton from './MagazineNewButton';
import { useAuth } from '../../hooks/useAuth';
import { archiveProposal, getProposals, Proposal } from '../../api/proposalService';
import { getCompanys } from '../../api/companysService';
import { getPublications, getIssues } from '../../api/magazineService';
import { getUsers } from '../../api/usersService';

const statusClass: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  sent: 'bg-blue-50 text-blue-700',
  signed: 'bg-green-50 text-green-700',
  cancelled: 'bg-orange-50 text-orange-700',
  archived: 'bg-gray-100 text-gray-500',
};

const formatMoney = (amount?: number, currency = 'gbp') => {
  if (amount == null || Number.isNaN(amount)) return '—';
  return new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency.toUpperCase() }).format(amount);
};

const lineSummary = (proposal: Proposal) => {
  const lines = proposal.line_items || [];
  if (!lines.length) return '—';
  if (lines.length === 1) return lines[0].label;
  return `${lines.length} items`;
};

const Proposals = () => {
  const { isAdmin, canEditAnyDeal } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState<Proposal[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [archivedFilter, setArchivedFilter] = useState('false');
  const [companyFilter, setCompanyFilter] = useState('');
  const [publicationFilter, setPublicationFilter] = useState('');
  const [issueFilter, setIssueFilter] = useState('');
  const [salesRepFilter, setSalesRepFilter] = useState('');
  const [companies, setCompanies] = useState<any[]>([]);
  const [publications, setPublications] = useState<any[]>([]);
  const [issues, setIssues] = useState<any[]>([]);
  const [salesReps, setSalesReps] = useState<any[]>([]);

  useEffect(() => {
    getCompanys('').then(setCompanies);
    getPublications().then(setPublications);
    if (canEditAnyDeal) {
      getUsers().then((users: any[]) => {
        const reps = (users || []).filter((u) =>
          (u.roles || []).some((r: any) =>
            ['sales_manager', 'sales_rep', 'ROLE_SALES_MANAGER', 'ROLE_SALES_REP'].includes(
              typeof r === 'string' ? r : r.name
            )
          )
        );
        setSalesReps(reps);
      });
    }
  }, [canEditAnyDeal]);

  useEffect(() => {
    if (publicationFilter) {
      getIssues(publicationFilter).then(setIssues);
    } else {
      setIssues([]);
      setIssueFilter('');
    }
  }, [publicationFilter]);

  const load = async () => {
    setLoading(true);
    const params: Record<string, string> = { archived: archivedFilter };
    if (statusFilter) params.status = statusFilter;
    if (companyFilter) params.company_id = companyFilter;
    if (publicationFilter) params.publication_id = publicationFilter;
    if (issueFilter) params.issue_id = issueFilter;
    if (salesRepFilter && canEditAnyDeal) params.sales_rep_id = salesRepFilter;
    const data = await getProposals(params);
    setItems(data);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, [statusFilter, archivedFilter, companyFilter, publicationFilter, issueFilter, salesRepFilter]);

  const companyOptions = useMemo(
    () => [...companies].sort((a, b) => a.name.localeCompare(b.name)),
    [companies]
  );

  const handleArchive = async (id: number) => {
    if (!isAdmin || !window.confirm('Archive this proposal?')) return;
    await archiveProposal(id);
    load();
  };

  return (
    <Layout>
      <MagazinePageHeader
        title="Proposals"
        description={canEditAnyDeal
          ? 'Build multi-item advertising proposals and send for signature.'
          : 'Create and manage your advertising proposals.'}
        action={<MagazineNewButton onClick={() => navigate('/magazine/proposals/new')} label="New proposal" />}
      />

      <PageCard>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <label className="text-sm text-gray-600">
            Status
            <select
              className="ml-2 rounded border border-gray-300 px-2 py-1 text-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All</option>
              <option value="draft">Draft</option>
              <option value="sent">Sent</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Company
            <select
              className="ml-2 max-w-[180px] rounded border border-gray-300 px-2 py-1 text-sm"
              value={companyFilter}
              onChange={(e) => setCompanyFilter(e.target.value)}
            >
              <option value="">All</option>
              {companyOptions.map((c) => (
                <option key={c.id} value={String(c.id)}>{c.name}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Publication
            <select
              className="ml-2 max-w-[160px] rounded border border-gray-300 px-2 py-1 text-sm"
              value={publicationFilter}
              onChange={(e) => setPublicationFilter(e.target.value)}
            >
              <option value="">All</option>
              {publications.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>
          </label>
          <label className="text-sm text-gray-600">
            Issue
            <select
              className="ml-2 max-w-[160px] rounded border border-gray-300 px-2 py-1 text-sm disabled:bg-gray-50"
              value={issueFilter}
              onChange={(e) => setIssueFilter(e.target.value)}
              disabled={!publicationFilter}
            >
              <option value="">All</option>
              {issues.map((i) => (
                <option key={i.id} value={String(i.id)}>{i.title}</option>
              ))}
            </select>
          </label>
          {canEditAnyDeal && (
            <label className="text-sm text-gray-600">
              Sales rep
              <select
                className="ml-2 max-w-[160px] rounded border border-gray-300 px-2 py-1 text-sm"
                value={salesRepFilter}
                onChange={(e) => setSalesRepFilter(e.target.value)}
              >
                <option value="">All</option>
                {salesReps.map((u) => (
                  <option key={u.id} value={String(u.id)}>
                    {u.username || u.email}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label className="text-sm text-gray-600">
            Show
            <select
              className="ml-2 rounded border border-gray-300 px-2 py-1 text-sm"
              value={archivedFilter}
              onChange={(e) => setArchivedFilter(e.target.value)}
            >
              <option value="false">Active</option>
              <option value="true">Archived</option>
              <option value="all">All</option>
            </select>
          </label>
        </div>

        {loading ? (
          <p className="text-sm text-gray-500">Loading...</p>
        ) : items.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500">No proposals yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-gray-500">
                  <th className="py-2 pr-4">Company</th>
                  <th className="py-2 pr-4">Items</th>
                  <th className="py-2 pr-4">Total</th>
                  {canEditAnyDeal && <th className="py-2 pr-4">Sales rep</th>}
                  <th className="py-2 pr-4">Status</th>
                  <th className="py-2">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((item) => (
                  <tr key={item.id} className="border-b border-gray-100">
                    <td className="py-3 pr-4 font-medium text-gray-900">
                      {item.company?.name || '—'}
                    </td>
                    <td className="py-3 pr-4 text-gray-600">{lineSummary(item)}</td>
                    <td className="py-3 pr-4 text-gray-900">{formatMoney(item.total_price, item.currency)}</td>
                    {canEditAnyDeal && (
                      <td className="py-3 pr-4 text-gray-600">
                        {item.sales_rep?.username || item.sales_rep?.email || '—'}
                      </td>
                    )}
                    <td className="py-3 pr-4">
                      <span className={`rounded px-2 py-0.5 text-xs font-medium capitalize ${statusClass[item.status] || ''}`}>
                        {item.archived_at ? 'archived' : item.status}
                      </span>
                    </td>
                    <td className="py-3">
                      <div className="flex flex-wrap gap-2">
                        <Link to={`/magazine/proposals/${item.id}`} className="text-sm text-torch-primary hover:underline">
                          View
                        </Link>
                        {isAdmin && !item.archived_at && (
                          <button
                            type="button"
                            onClick={() => handleArchive(item.id)}
                            className="text-sm text-gray-600 hover:underline"
                          >
                            Archive
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </PageCard>
    </Layout>
  );
};

export default Proposals;
